import { useState, useEffect } from "react";
import { Users, Plus, Trash2, X, BarChart3, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAgents,
  createAgent,
  resendInvite,
  deleteAgent,
  getAgentStats,
  readApiError,
} from "./supportApi";
import { CopyHash } from "../../features/web3/forex/components/CopyHash";
import SupportConfirmationModal from "./SupportConfirmationModal";

export default function AgentDashboard() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [resendingAgentId, setResendingAgentId] = useState(null);

  // Set once an invitation has been issued, by creating an agent or by re-sending. The link
  // is the whole point: the server cannot confirm the email arrived, so this is the only copy
  // an admin can hand over when it does not. Both paths answer the same shape, so one piece
  // of state and one view cover both.
  const [inviteInfo, setInviteInfo] = useState(null);
  const [agentFormData, setAgentFormData] = useState({
    email: "",
    name: "",
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);

  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const data = await getAgents(1, 20);
      setAgents(data.agents || []);
    } catch (err) {
      console.error("Error fetching agents:", err);
      setError("Failed to fetch agents");
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleAddAgent = async (e) => {
    e.preventDefault();
    setIsAgentLoading(true);

    try {
      const response = await createAgent(
        agentFormData.email,
        agentFormData.name,
      );

      if (response.status === 201) {
        // The modal deliberately stays open — it swaps to the invitation link, and closing
        // here would throw away the only copy of it. No toast either; the modal says it.
        const { agent, inviteUrl, expiresAt } = await response.json();
        setInviteInfo({ email: agent.email, inviteUrl, expiresAt });
        await fetchAgents();
      } else {
        // Two refusals worth reading: a 409 means that address already has an agent, and a
        // 400 means it is not on a company domain — and only the server knows which domains
        // those are. Both arrive as the server's own wording.
        toast.error(await readApiError(response, "Could not create the agent"));
        setIsAgentModalOpen(false);
      }
    } catch (error) {
      console.error("Error creating agent:", error);
      toast.error("Could not create the agent. Please try again.");
      setIsAgentModalOpen(false);
    } finally {
      setIsAgentLoading(false);
    }

    setAgentFormData({ email: "", name: "" });
  };

  const handleResendInvite = async (agent) => {
    setResendingAgentId(agent.id);
    try {
      const response = await resendInvite(agent.id);

      if (response.status === 200) {
        const { inviteUrl, expiresAt } = await response.json();
        setInviteInfo({ email: agent.email, inviteUrl, expiresAt });
        setIsAgentModalOpen(true);
      } else {
        toast.error(await readApiError(response, "Could not re-send the invitation"));
      }
    } catch (error) {
      console.error("Error re-sending invitation:", error);
      toast.error("Could not re-send the invitation. Please try again.");
    } finally {
      setResendingAgentId(null);
    }
  };

  const closeAgentModal = () => {
    setIsAgentModalOpen(false);
    setInviteInfo(null);
  };

  const handleDeleteAgent = async (id) => {
    setDeletingAgentId(id);
    try {
      const response = await deleteAgent(id);

      if (response.status === 204) {
        toast.success("Agent deleted successfully!");
        await fetchAgents();
      } else {
        // 404 said "Agent not found" here; that is what the server sends back anyway.
        toast.error(await readApiError(response, "Could not delete the agent"));
      }
    } catch (error) {
      console.error("Error deleting agent:", error);
      toast.error("Could not delete the agent. Please try again.");
    } finally {
      setDeletingAgentId(null);
      setShowDeleteModal(false);
      setAgentToDelete(null);
    }
  };

  const handleDeleteAgentClick = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (agentToDelete) {
      handleDeleteAgent(agentToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setAgentToDelete(null);
  };

  const handleViewStats = async (agent) => {
    setSelectedAgent(agent);
    setShowStatsModal(true);
    setStatsLoading(true);
    setStatsError(null);
    setStatsData(null);

    try {
      const data = await getAgentStats(agent.id);
      setStatsData(data);
    } catch (error) {
      console.error("Error fetching agent stats:", error);
      setStatsError("Failed to load statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCloseStatsModal = () => {
    setShowStatsModal(false);
    setSelectedAgent(null);
    setStatsData(null);
    setStatsError(null);
  };

  return (
    <div className="">
      <div className="container mx-auto">
        <main className="w-full">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
                  <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
                  Support
                </div>
                <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
                  Agent <span className="text-gradient-ginox">Management</span>
                </h1>
                <p className="text-sm text-muted-foreground mb-4">
                  Manage your support team
                </p>
              </div>

              <div className="flex items-center gap-2 mb-6">
                <Link
                  to="/support-system/dashboard"
                  className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                >
                  ← Back to Dashboard
                </Link>
                <button
                  onClick={() => {
                    setAgentFormData({ email: "", name: "" });
                    setInviteInfo(null);
                    setIsAgentModalOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
                >
                  <Plus size={14} aria-hidden="true" />
                  <span>Add Agent</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div>
              {loading && (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
                </div>
              )}

              {error && (
                <div className="flex items-center justify-center h-64">
                  <div className="text-red-400 text-sm">{error}</div>
                </div>
              )}

              {!loading && !error && agents.length > 0 && (
                <div className="md:grid gap-4 md:grid-cols-2 lg:grid-cols-3 space-y-4 md:space-y-0">
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className="rounded-lg border border-border/70 bg-card p-5 hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-ginox-green/10 border border-ginox-green/20 flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4 text-ginox-green" aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-foreground text-sm truncate">
                              {agent.name} ({agent.email})
                            </h3>
                            <div className="flex items-center gap-2 capitalize">
                              <p className="text-xs text-muted-foreground truncate">
                                {agent.status || "N/A"}
                              </p>
                              {/* Permission, not presence: an invited agent is a real row
                                  that can hold tickets, but nobody can sign in as it yet. */}
                              <span
                                className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[10px] ${
                                  agent.accountStatus === "active"
                                    ? "border-ginox-green/30 bg-ginox-green/10 text-ginox-green"
                                    : "border-amber-500/30 bg-amber-500/10 text-amber-400"
                                }`}
                              >
                                {agent.accountStatus}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5 mb-4 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Created:</span>
                          <span className="font-mono text-foreground">
                            {agent.createdAt
                              ? new Date(agent.createdAt).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewStats(agent)}
                          className="flex-1 flex items-center justify-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
                        >
                          <BarChart3 size={14} aria-hidden="true" />
                          Stats
                        </button>
                        <button
                          onClick={() => handleResendInvite(agent)}
                          disabled={resendingAgentId === agent.id}
                          title="Issues a fresh link and expires the old one. Also the way back in for an agent who forgot their password — there is no self-serve reset."
                          className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                            resendingAgentId === agent.id
                              ? "border-border/40 bg-white/[0.02] text-muted-foreground cursor-not-allowed"
                              : "border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                          }`}
                        >
                          <Mail size={14} aria-hidden="true" />
                          {resendingAgentId === agent.id ? "Sending..." : "Re-send invite"}
                        </button>
                        <button
                          onClick={() => handleDeleteAgentClick(agent)}
                          disabled={deletingAgentId === agent.id}
                          className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                            deletingAgentId === agent.id
                              ? "border-border/40 bg-white/[0.02] text-muted-foreground cursor-not-allowed"
                              : "border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                          }`}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                          {deletingAgentId === agent.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && !error && agents.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-10">No agents found</p>
              )}
            </div>
          </div>
        </main>

        {/* Add Agent Modal */}
        {isAgentModalOpen && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="rounded-lg border border-border/70 bg-card p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-foreground">
                  {inviteInfo ? "Invitation sent" : "Add New Agent"}
                </h3>
                <button
                  onClick={closeAgentModal}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>

              {inviteInfo ? (
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    An invitation has been emailed to{" "}
                    <span className="font-mono text-foreground">{inviteInfo.email}</span>.
                    Delivery cannot be confirmed from here, so keep this link in case it
                    never arrives — it is the only other copy.
                  </p>

                  <div className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-2 text-xs">
                    <CopyHash value={inviteInfo.inviteUrl} head={34} tail={8} />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Expires{" "}
                    <span className="font-mono text-foreground">
                      {new Date(inviteInfo.expiresAt).toLocaleString()}
                    </span>
                    . Re-sending the invitation issues a new link and expires this one.
                  </p>

                  <button
                    type="button"
                    onClick={closeAgentModal}
                    className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
              <form onSubmit={handleAddAgent} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={agentFormData.email}
                    onChange={(e) =>
                      setAgentFormData({
                        ...agentFormData,
                        email: e.target.value,
                      })
                    }
                    placeholder="name@ginox.io"
                    className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Name
                  </label>
                  <input
                    type="text"
                    value={agentFormData.name}
                    onChange={(e) =>
                      setAgentFormData({
                        ...agentFormData,
                        name: e.target.value,
                      })
                    }
                    placeholder="Enter full name"
                    className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                    required
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeAgentModal}
                    className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isAgentLoading}
                    className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                      isAgentLoading
                        ? "border-border/40 bg-white/[0.02] text-muted-foreground cursor-not-allowed"
                        : "border-ginox-green/60 bg-ginox-green/10 text-ginox-green hover:bg-ginox-green/20"
                    }`}
                  >
                    {isAgentLoading ? "Processing..." : "Create"}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <SupportConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Agent"
          message={`Are you sure you want to delete agent "${agentToDelete?.email}"? This action cannot be undone.`}
          confirmText="Yes, Delete"
          cancelText="No, Cancel"
          loading={deletingAgentId === agentToDelete?.id}
          loadingText="Deleting..."
        />

        {/* Stats Modal */}
        {showStatsModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="rounded-lg border border-border/70 bg-card p-6 w-full max-w-lg mx-4">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Agent Statistics
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 font-mono">
                    {selectedAgent?.name} ({selectedAgent?.email})
                  </p>
                </div>
                <button
                  onClick={handleCloseStatsModal}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>

              {statsLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
                </div>
              )}

              {statsError && (
                <div className="flex items-center justify-center py-12">
                  <p className="text-sm text-red-400 text-center">{statsError}</p>
                </div>
              )}

              {!statsLoading && !statsError && statsData && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-border/70 bg-card p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                      Tickets Assigned
                    </p>
                    <p className="text-2xl font-mono text-foreground">
                      {statsData.ticketsAssigned ?? "N/A"}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border/70 bg-card p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                      Tickets Open
                    </p>
                    <p className="text-2xl font-mono text-foreground">
                      {statsData.ticketsOpen ?? "N/A"}
                    </p>
                  </div>

                  <div className="rounded-lg border border-border/70 bg-card p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">
                      Chats Handled
                    </p>
                    <p className="text-2xl font-mono text-foreground">
                      {statsData.chatsHandled ?? "N/A"}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <button
                  onClick={handleCloseStatsModal}
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
